import React from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { useSnackbar } from "notistack";
import { LoadingButton, ShowcasePageTitle } from "@components";
import { LoginPagesLayout } from "@features/login/LoginPagesLayout";
import { renderFormElement } from "@helpers/renderFormElement";
import { useAssignPassword } from "@features/login/reset/useAssignPassword";

interface AssignPasswordFormFields {
  password: string;
}

export const AssignPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { mutate: assignPassword, isPending } = useAssignPassword();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const confirmationToken = searchParams.get("confirmationToken") || "";

  const assignPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required(t("form.errors.required"))
      .min(8, t("form.errors.passwordMinLength")),
  });

  const { handleSubmit, control } = useForm<AssignPasswordFormFields>({
    mode: "onSubmit",
    resolver: yupResolver(assignPasswordSchema),
    defaultValues: {
      password: "",
    },
  });

  const onSubmit = (form: AssignPasswordFormFields) => {
    assignPassword(
      {
        password: form.password,
        confirmationToken,
      },
      {
        onSuccess: () => {
          enqueueSnackbar({
            message: t("assignPasswordPage.success"),
            variant: "success",
          });
          setTimeout(() => {
            navigate("/");
          }, 1000);
        },
        onError: (err) => {
          enqueueSnackbar({
            message:
              err.response?.data.message || t("assignPasswordPage.error"),
            variant: "error",
          });
        },
      }
    );
  };

  if (!confirmationToken) {
    return <Navigate to="/" replace />;
  }

  return (
    <LoginPagesLayout>
      <ShowcasePageTitle title={t("assignPasswordPage.header.title")} />

      {renderFormElement(
        "assignPasswordPage.form",
        {
          type: "password",
          name: "password",
        },
        control,
        {
          showPasswordStrength: true,
        }
      )}
      <LoadingButton isFetching={isPending} onClick={handleSubmit(onSubmit)}>
        {t("assignPasswordPage.form.submit")}
      </LoadingButton>
    </LoginPagesLayout>
  );
};
